/**
 * Finds the robot's switch on the local network, with no setup: the address it last answered on,
 * then the Shelly's own Wi-Fi address, then every address on the tablet's hotspot network. A
 * device only counts if it reports the robot's own Shelly id.
 */
import type { HttpTransport } from './switch';

/** Where a Shelly answers on its own Wi-Fi access point. */
export const SHELLY_AP_ADDRESS = '192.168.33.1';

export interface LocalAddress {
  readonly address: string;
  readonly prefixLength: number;
}

/** True when the device at `address` is the Shelly with this id (12 hex characters). */
export async function isOurSwitch(
  address: string,
  deviceId: string,
  transport: HttpTransport,
  timeoutMs: number,
): Promise<boolean> {
  try {
    // /shelly answers without authentication: name, id, MAC.
    const response = await transport({ url: `http://${address}/shelly`, headers: {}, timeoutMs });
    if (response.status !== 200) return false;
    const info = JSON.parse(response.body) as { id?: unknown; mac?: unknown };
    const want = deviceId.toLowerCase();
    return (
      (typeof info.mac === 'string' && info.mac.toLowerCase() === want) ||
      (typeof info.id === 'string' && info.id.toLowerCase().endsWith(`-${want}`))
    );
  } catch {
    return false;
  }
}

/** Every other host on the /24 of each IPv4 address the tablet has (its hotspot, its Wi-Fi). */
export function subnetHosts(addresses: readonly LocalAddress[]): string[] {
  const hosts: string[] = [];
  for (const { address, prefixLength } of addresses) {
    const parts = address.split('.').map(Number);
    if (parts.length !== 4 || parts.some((p) => !Number.isInteger(p) || p < 0 || p > 255)) continue;
    if (prefixLength < 16 || prefixLength > 30 || parts[0] === 127) continue;
    const base = parts.slice(0, 3).join('.');
    for (let host = 1; host < 255; host++) {
      const candidate = `${base}.${host}`;
      if (candidate !== address && !hosts.includes(candidate)) hosts.push(candidate);
    }
  }
  return hosts;
}

export interface FindSwitchOptions {
  readonly deviceId: string;
  /** The address it answered on last time. */
  readonly known: string | null;
  readonly localAddresses: () => Promise<readonly LocalAddress[]>;
  readonly transport: HttpTransport;
  readonly probeTimeoutMs?: number;
  readonly concurrency?: number;
}

export async function findSwitch({
  deviceId,
  known,
  localAddresses,
  transport,
  probeTimeoutMs = 700,
  concurrency = 32,
}: FindSwitchOptions): Promise<string | null> {
  const first = [...new Set([known, SHELLY_AP_ADDRESS].filter((a): a is string => a !== null))];
  for (const address of first) {
    if (await isOurSwitch(address, deviceId, transport, probeTimeoutMs * 2)) return address;
  }
  const hosts = subnetHosts(await localAddresses()).filter((h) => !first.includes(h));
  for (let i = 0; i < hosts.length; i += concurrency) {
    const batch = hosts.slice(i, i + concurrency);
    const found = await Promise.all(
      batch.map(async (h) =>
        (await isOurSwitch(h, deviceId, transport, probeTimeoutMs)) ? h : null,
      ),
    );
    const hit = found.find((h) => h !== null);
    if (hit) return hit;
  }
  return null;
}
